import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchForm } from '@/components/search/SearchForm';
import { SearchResults } from '@/components/search/SearchResults';
import { SearchSummary } from '@/components/search/SearchSummary';
import { RelatedPersons } from '@/components/search/RelatedPersons';
import { DebugPanel } from '@/components/search/DebugPanel';
import { searchAPI } from '@/api/search';
import type { SearchResponse } from '@/api/search';

export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [lastQuery, setLastQuery] = useState<string>('');

  // Внутренняя функция поиска (без обновления URL)
  const handleSearchInternal = useCallback(async (query: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await searchAPI.performSearch(query);
      
      if (response.success) {
        setSearchResults(response);
        setLastQuery(query);
        // Сохраняем результаты в sessionStorage
        sessionStorage.setItem('searchResults', JSON.stringify(response));
        sessionStorage.setItem('lastSearchQuery', query);
      } else {
        setError(response.error || 'Произошла ошибка при поиске');
        setSearchResults(null);
        sessionStorage.removeItem('searchResults');
        sessionStorage.removeItem('lastSearchQuery');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Произошла ошибка при поиске');
      setSearchResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Основная функция поиска (обновляет URL)
  const handleSearch = async (query: string) => {
    // Обновляем URL с параметром поиска
    setSearchParams({ q: query });
    
    // Выполняем поиск
    await handleSearchInternal(query);
  };

  // Проверка URL параметров и автоматический поиск при загрузке
  useEffect(() => {
    const queryFromUrl = searchParams.get('q') || searchParams.get('query');
    
    if (queryFromUrl) {
      // Если есть запрос в URL, выполняем поиск
      setLastQuery(queryFromUrl);
      handleSearchInternal(queryFromUrl);
    } else {
      // Если нет запроса в URL, пытаемся восстановить из sessionStorage
      const savedResults = sessionStorage.getItem('searchResults');
      const savedQuery = sessionStorage.getItem('lastSearchQuery');
      
      if (savedResults && savedQuery) {
        try {
          const parsedResults = JSON.parse(savedResults);
          setSearchResults(parsedResults);
          setLastQuery(savedQuery);
        } catch (err) {
          console.error('Ошибка при восстановлении результатов поиска:', err);
          sessionStorage.removeItem('searchResults');
          sessionStorage.removeItem('lastSearchQuery');
        }
      }
    }
  }, [searchParams, handleSearchInternal]);

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Поиск людей</h1>
          {lastQuery && (
            <p className="text-muted-foreground mt-2">
              Последний поиск: "{lastQuery}"
            </p>
          )}
        </div>

        <SearchForm 
          onSearch={handleSearch} 
          isLoading={isLoading} 
          initialQuery={lastQuery}
        />

        {error && (
          <div className="bg-destructive/15 text-destructive px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {searchResults && (
          <>
            <SearchSummary
              results={searchResults.results_by_source}
              operatorInfo={searchResults.operator_info}
              totalResults={searchResults.results_count}
            />
            <RelatedPersons
              results={searchResults.results_by_source}
              searchQuery={lastQuery}
            />
            <SearchResults
              results={searchResults.results_by_source}
              operatorInfo={searchResults.operator_info}
              queryTime={searchResults.query_time_ms}
              totalResults={searchResults.results_count}
            />
          </>
        )}

        <DebugPanel 
          searchResponse={searchResults} 
          isVisible={showDebug} 
        />
      </div>
    </div>
  );
}
